import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useCustomer } from '../../../../contexts/CustomerContext';
import styles from './PreviousImages.module.scss';
import PastImagesContent from '../../../PastImagesContent';
import routes from '../../../../routes';
import useQueryString from '../../../../hooks/use-query-string';
import useStudent from '../../../../hooks/useStudent';
import useAccessCodePersister from '../../../../hooks/useAccessCodePersister';
import Button from "../../../Button/v2";
import usePastImagesQuickOrderPricelist from '../../../../hooks/usePastImagesQuickOrderPricelist';

// Add a function to get the current domain
const getCurrentDomain = () => {
  return window.location.origin;
};

function PreviousImages({ toReview }) {
  const { customer, setCustomer } = useCustomer();
  const history = useHistory();
  const location = useLocation();
  const [hasNoPastImages, setHasNoPastImages] = useState(false);
  const { access_code: accessCode, message_id: messageId, from } = useQueryString();
  const schoolId = customer?.currentStudent?.schoolId || customer?.currentSchool?.id;
  const { data: quickOrderPricelist, isLoading: isLoadingQuickOrder } = usePastImagesQuickOrderPricelist(schoolId);

  useEffect(() => {
    // Check domain and redirect if needed
    if (getCurrentDomain() === 'https://www.shoob.cloud')  {
      history.push(routes.review());
      return;
    }

    if (!accessCode) return;

    localStorage.setItem('isUsingAccessCode', 'true');
  }, [accessCode, history]);

  // If the school is assigned the new past-images-quick-order pricelist,
  // send the customer to the new fast-lane page instead of rendering the
  // legacy multi-step flow. Preserve toReview so the new page knows it's
  // being shown as a post-purchase upsell (allowed to skip) vs as the
  // customer's primary purchase (must pick something).
  //
  // Note: the senior Quick Order page (/c/senior-past-images-order) is
  // intentionally NOT detected here. Senior email campaigns link to that
  // URL directly so this elementary-facing route stays untouched.
  useEffect(() => {
    if (isLoadingQuickOrder) return;
    if (!quickOrderPricelist) return;
    // Forward the campaign-link attribution params: this redirect used to
    // strip message_id/from, orphaning every elementary past-images campaign
    // purchase from the send that produced it.
    history.replace(routes.pastImages.quickOrder({
      ...(toReview ? { toReview: 'true' } : {}),
      ...(messageId ? { message_id: messageId } : {}),
      ...(from ? { from } : {}),
    }));
  }, [isLoadingQuickOrder, quickOrderPricelist, history, toReview, messageId, from]);
  
  const isPastImages = location.pathname.includes('past_images');
  const isDownloadImages = location.pathname.includes('download-image');
  const cartKey = customer.priorityCart ? 'priorityCart' : 'cart';
  
  const handleContinue = ({ packageId, studentImage }) => {
    localStorage.setItem(`currentSeason-${studentImage.studentId}`, studentImage.folder);
    history.push(routes.elementaryMultiPoseBuilder(packageId, { step: 'select-package' }));
  };
  
  const handleReview = () => {
    history.push(routes.review());
  }
  
  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <PastImagesContent
          accessCode={accessCode}
          onContinue={handleContinue}
          // No sellable past images -> the pitch page is pointless; skip
          // straight to checkout instead of a header over an empty grid.
          onEmpty={handleReview}
          student={customer.currentStudent}
        />
      </div>
      
      {toReview && (
        <div className={styles.btnsContainer}>
          <Button variant="" onClick={handleReview}>Continue to Checkout</Button>
        </div>
      )}
    </div>
  );
}

export default function LandableWrapper() {
  const { sch, sid, toReview, access_code: accessCode } = useQueryString();
  const { data: student } = useStudent({ studentId: sid, schoolId: sch, accessCode });
  const { customer, setCustomer } = useCustomer();
  const history = useHistory(); // Add history
  
  const { isLoading } = useAccessCodePersister({ accessCode, schoolId: sch });
  
  useEffect(() => {
    // Check domain and redirect if needed
    if (getCurrentDomain() === 'https://www.shoob.cloud') {
      history.push(routes.review());
      return;
    }
    
    if (!student) return;
    
    setCustomer({ ...customer, currentStudent: student, currentSchool: student.school });
  }, [student, history, customer, setCustomer]);
  
  const isSchoolSet = customer?.currentSchool?.id?.toString() === sch?.toString();
  const isStudentSet = customer?.currentStudent?.studentId?.toString() === sid?.toString();
  
  const skipLandableCheck = !(sch || sid);
  
  if (!skipLandableCheck && !(isSchoolSet && isStudentSet)) return null;
  if (accessCode && isLoading) return null;
  
  return (
    <PreviousImages toReview={toReview} />
  );
}